import { useUnifiedStatus } from "@/contexts/UnifiedStatusContext";
import { UnifiedStatusBadge } from "@/components/shared/UnifiedStatusBadge";
import { useState } from "react";
import { motion } from "framer-motion";
import { Heart, ChevronRight, Star, Clock, Gift, ArrowRight, ExternalLink, Bookmark, Sparkles } from "lucide-react";

const MiniLogo = ({ name, size = "md" }) => {
  const colors = ["bg-sky-400", "bg-purple-400", "bg-green-400", "bg-orange-400", "bg-pink-400", "bg-indigo-400", "bg-teal-400", "bg-red-400"];
  const i = name ? name.charCodeAt(0) % colors.length : 0;
  const s = size === "sm" ? "w-9 h-9 text-xs" : size === "lg" ? "w-20 h-20 text-2xl rounded-3xl" : "w-11 h-11 text-sm";
  return <div className={`${s} rounded-2xl ${colors[i]} flex items-center justify-center flex-shrink-0`}><span className="text-white font-bold">{name?.charAt(0)?.toUpperCase()}</span></div>;
};

export const MagazineView = ({ data, user, onOfferClick }) => {
  const { getStatus } = useUnifiedStatus();
  const { offers, expiringSoon, bestValue, stats, favoriteIds, toggleFavorite } = data;
  const totalValue = offers.reduce((s, o) => s + (o.offer_value_est || 0), 0);

  // Featured offer — highest value
  const featured = bestValue[0] || offers[0];
  // Editor's picks — varied selection
  const picks = offers.filter(o => o.business_id !== featured?.business_id).slice(0, 6);
  // Category groups for "sections"
  const sections = {};
  offers.forEach(o => {
    if (!sections[o.category]) sections[o.category] = [];
    sections[o.category].push(o);
  });
  const topSections = Object.entries(sections).sort((a, b) => b[1].length - a[1].length).slice(0, 3);

  return (
    <div className="min-h-screen bg-white pb-28" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Magazine header */}
      <div className="px-6 pt-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Birthday<span className="text-sky-500">Deals</span>
          </h1>
          <p className="text-xs text-slate-400 mt-0.5">{stats.days_until_birthday} days to go &middot; ${totalValue.toFixed(0)} in free stuff</p>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-green-500" />
          <span className="text-xs text-slate-400">{offers.length} live</span>
        </div>
      </div>

      {/* Featured hero card */}
      {featured && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          onClick={() => onOfferClick(featured)}
          className="mx-6 mt-6 bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-6 text-white cursor-pointer relative overflow-hidden group"
          data-testid="mag-featured"
        >
          <div className="absolute -top-8 -right-8 w-32 h-32 bg-sky-500/20 rounded-full blur-2xl" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-purple-500/10 rounded-full blur-xl" />
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-4 h-4 text-sky-400" />
              <span className="text-xs font-bold text-sky-400 uppercase tracking-widest">Featured</span>
            </div>
            <div className="flex items-center gap-4">
              <MiniLogo name={featured.name} size="lg" />
              <div className="flex-1">
                <h2 className="text-2xl font-extrabold" style={{ fontFamily: "'Outfit', sans-serif" }}>{featured.name}</h2>
                {getStatus(featured.business_id) && <UnifiedStatusBadge status={getStatus(featured.business_id)} />}
                <p className="text-white/60 text-sm mt-1">{featured.offer_title}</p>
              </div>
            </div>
            <div className="flex items-center justify-between mt-5 pt-4 border-t border-white/10">
              <div className="flex items-center gap-4">
                <span className="text-2xl font-extrabold text-green-400">${featured.offer_value_est}</span>
                <span className="text-xs text-white/40">{featured.category}</span>
              </div>
              <ChevronRight className="w-5 h-5 text-white/40 group-hover:text-white transition-colors" />
            </div>
          </div>
        </motion.div>
      )}

      {/* Quick stats row */}
      <div className="px-6 mt-6 flex gap-3">
        <div className="flex-1 bg-red-50 rounded-2xl p-3 text-center">
          <Clock className="w-4 h-4 text-red-400 mx-auto mb-1" />
          <p className="text-lg font-bold text-red-600">{expiringSoon.length}</p>
          <p className="text-[10px] text-red-400">Expiring</p>
        </div>
        <div className="flex-1 bg-amber-50 rounded-2xl p-3 text-center">
          <Star className="w-4 h-4 text-amber-400 mx-auto mb-1" />
          <p className="text-lg font-bold text-amber-600">{bestValue.length}</p>
          <p className="text-[10px] text-amber-400">Best Value</p>
        </div>
        <div className="flex-1 bg-sky-50 rounded-2xl p-3 text-center">
          <Gift className="w-4 h-4 text-sky-400 mx-auto mb-1" />
          <p className="text-lg font-bold text-sky-600">{offers.length}</p>
          <p className="text-[10px] text-sky-400">Total</p>
        </div>
        <div className="flex-1 bg-pink-50 rounded-2xl p-3 text-center">
          <Heart className="w-4 h-4 text-pink-400 mx-auto mb-1" />
          <p className="text-lg font-bold text-pink-600">{favoriteIds.size}</p>
          <p className="text-[10px] text-pink-400">Saved</p>
        </div>
      </div>

      {/* Expiring ribbon */}
      {expiringSoon.length > 0 && (
        <div className="mt-6">
          <div className="px-6 flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Expiring Soon</h2>
          </div>
          <div className="flex gap-3 overflow-x-auto px-6 pb-2 scrollbar-hide">
            {expiringSoon.slice(0, 5).map(offer => (
              <motion.div
                key={offer.business_id}
                whileTap={{ scale: 0.95 }}
                onClick={() => onOfferClick(offer)}
                className="flex-shrink-0 w-40 bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-4 border border-red-100 cursor-pointer"
                data-testid={`mag-exp-${offer.business_id}`}
              >
                <MiniLogo name={offer.name} size="sm" />
                <p className="font-semibold text-slate-800 text-sm mt-2 truncate">{offer.name}</p>
                <p className="text-xs text-slate-400 truncate">{offer.offer_title}</p>
                <p className="text-sm font-bold text-green-600 mt-1">${offer.offer_value_est}</p>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Category sections */}
      {topSections.map(([cat, catOffers]) => (
        <div key={cat} className="mt-6 px-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>{cat}</h2>
            <span className="text-xs text-slate-400">{catOffers.length}</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {catOffers.slice(0, 4).map(offer => (
              <motion.div
                key={offer.business_id}
                whileTap={{ scale: 0.97 }}
                onClick={() => onOfferClick(offer)}
                className="bg-slate-50 rounded-2xl p-3.5 cursor-pointer hover:bg-slate-100 transition-colors"
                data-testid={`mag-offer-${offer.business_id}`}
              >
                <MiniLogo name={offer.name} size="sm" />
                <p className="text-sm font-semibold text-slate-800 mt-2 truncate">{offer.name}</p>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-sm font-bold text-green-600">${offer.offer_value_est}</span>
                  <button onClick={(e) => { e.stopPropagation(); toggleFavorite(offer.business_id); }} className="p-0.5">
                    <Heart className={`w-3.5 h-3.5 ${favoriteIds.has(offer.business_id) ? "fill-red-500 text-red-500" : "text-slate-300"}`} />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      ))}

      {/* See all */}
      <div className="px-6 mt-8">
        <button className="w-full py-3.5 border-2 border-slate-200 rounded-2xl text-sm font-semibold text-slate-600 flex items-center justify-center gap-2 hover:border-slate-300 transition-colors" data-testid="mag-see-all">
          Browse All Deals <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
